import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MagnifyingGlassIcon, MapPinIcon, ArrowPathIcon, PhoneIcon } from '@heroicons/react/24/outline';
import CountryFlag from './CountryFlag';

const CountrySelectionModal = ({ isOpen, onClose, onSelectCountry, onShowNumberSearch, onShowTollFreeSearch, isLoading, subscriptionInfo, userId }) => {
  console.log('🎭 NEW CountrySelectionModal render - isOpen:', isOpen, 'isLoading:', isLoading);
  
  const [availability, setAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showUSOptions, setShowUSOptions] = useState(false);
  const [selectedUSOption, setSelectedUSOption] = useState(null);
  const [showRandomConfirmation, setShowRandomConfirmation] = useState(false);
  const [showCountryConfirmation, setShowCountryConfirmation] = useState(false);
  const [selectedCountryForConfirmation, setSelectedCountryForConfirmation] = useState(null);

  const countries = [
    { code: 'US', name: 'United States', dialingCode: '+1' },
    { code: 'CA', name: 'Canada', dialingCode: '+1' },
    { code: 'GB', name: 'United Kingdom', dialingCode: '+44' }
  ];

  // Check number availability when modal opens (only for CA and GB)
  useEffect(() => {
    if (isOpen && !loadingAvailability) {
      checkAvailability();
    } else if (!isOpen) {
      // Reset all states when modal closes
      setShowUSOptions(false);
      setSelectedUSOption(null);
      setShowRandomConfirmation(false);
      setShowCountryConfirmation(false);
      setSelectedCountryForConfirmation(null);
    }
  }, [isOpen]);

  const checkAvailability = async () => {
    setLoadingAvailability(true);
    try {
      const response = await fetch('/api/checkNumberAvailability');
      if (response.ok) {
        const data = await response.json();
        // US is always available (users can purchase via area code)
        setAvailability({ 
          US: true, 
          CA: data.CA || false, 
          GB: data.GB || false 
        });
      } else {
        console.error('Failed to check availability');
        // US always available, others unavailable on error
        setAvailability({ US: true, CA: false, GB: false });
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      // US always available, others unavailable on error
      setAvailability({ US: true, CA: false, GB: false });
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleCountryClick = (countryCode) => {
    console.log('🌍 Country selected:', countryCode);
    
    // If US is selected, show US-specific options
    if (countryCode === 'US') {
      setShowUSOptions(true);
    } else if (countryCode === 'CA' || countryCode === 'GB') {
      // For Canada and UK, show confirmation dialog
      setSelectedCountryForConfirmation(countries.find(c => c.code === countryCode));
      setShowCountryConfirmation(true);
    } else {
      // For other countries, proceed with normal flow
      onSelectCountry(countryCode);
    }
  };

  const handleUSOptionSelect = (option) => {
    console.log('🇺🇸 US option selected:', option);
    setSelectedUSOption(option);
    if (option === 'random') {
      // Show confirmation for random number
      setShowRandomConfirmation(true);
    } else if (option === 'area_code') {
      // Close this modal and show the number search modal
      onClose();
      if (onShowNumberSearch) {
        onShowNumberSearch();
      }
    } else if (option === 'toll_free') {
      // Close this modal and show the toll-free search modal
      onClose();
      if (onShowTollFreeSearch) {
        onShowTollFreeSearch();
      }
    }
  };

  // Check if user is eligible for toll-free numbers
  const isTollFreeEligible = () => {
    if (!subscriptionInfo?.priceId) return false;
    
    const tollFreePriceIds = [
      'price_1S35dPK5A174Olbc7miU9uUm',
      'price_1S35h6K5A174Olbc38lVIXdT'
    ];
    
    return tollFreePriceIds.includes(subscriptionInfo.priceId);
  };

  const handleRandomConfirm = () => {
    // Proceed with random number flow
    onSelectCountry('US');
  };

  const handleRandomBack = () => {
    setShowRandomConfirmation(false);
    setSelectedUSOption(null);
  };

  const handleBackToCountries = () => {
    setShowUSOptions(false);
    setSelectedUSOption(null);
  };

  const handleCountryConfirm = () => {
    // Proceed with the selected country
    if (selectedCountryForConfirmation) {
      onSelectCountry(selectedCountryForConfirmation.code);
    }
  };

  const handleCountryBack = () => {
    setShowCountryConfirmation(false);
    setSelectedCountryForConfirmation(null);
  };

  const handleCloseClick = () => {
    console.log('🚪 Close button clicked');
    // Reset all states when closing
    setShowUSOptions(false);
    setSelectedUSOption(null);
    setShowRandomConfirmation(false);
    setShowCountryConfirmation(false);
    setSelectedCountryForConfirmation(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-md w-full">
            {/* Liquid Glass Panel */}
            <div 
              className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 max-h-[85vh] flex flex-col p-6"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseClick}
                disabled={isLoading}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                style={{
                  opacity: isLoading ? 0.5 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="text-lg">×</span>
              </button>

              {/* Header */}
              <div className="mb-6">
                {showCountryConfirmation ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        onClick={handleCountryBack}
                        disabled={isLoading}
                        className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                      >
                        <span className="text-sm">←</span>
                      </button>
                      <h2 className="text-xl font-medium text-white">
                        Confirm {selectedCountryForConfirmation?.name}
                      </h2>
                    </div>
                    <p className="text-white/60 text-sm">
                      Confirm you want a phone number from {selectedCountryForConfirmation?.name}
                    </p>
                  </>
                ) : showRandomConfirmation ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        onClick={handleRandomBack}
                        disabled={isLoading}
                        className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                      >
                        <span className="text-sm">←</span>
                      </button>
                      <h2 className="text-xl font-medium text-white">
                        Confirm Random Number
                      </h2>
                    </div>
                    <p className="text-white/60 text-sm">
                      Are you sure you want a randomly assigned number?
                    </p>
                  </>
                ) : showUSOptions ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        onClick={handleBackToCountries}
                        className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                      >
                        <span className="text-sm">←</span>
                      </button>
                      <h2 className="text-xl font-medium text-white">
                        US Phone Number Options
                      </h2>
                    </div>
                    <p className="text-white/60 text-sm">
                      Choose how you'd like to get your US number
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-medium text-white mb-2">
                      Select Country
                    </h2>
                    <p className="text-white/60 text-sm">
                      Choose your phone number&apos;s region
                    </p>
                  </>
                )}
              </div>

              {/* Content Area */}
              <div className="space-y-3 flex-1 overflow-y-auto">
                {showCountryConfirmation ? (
                  /* Country Confirmation View */
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mx-auto mb-4">
                        <CountryFlag countryCode={selectedCountryForConfirmation?.code} size="lg" />
                      </div>
                      <div className="text-white/70 text-sm mb-6">
                        You're about to get a phone number from {selectedCountryForConfirmation?.name} ({selectedCountryForConfirmation?.dialingCode}). 
                        This number will be assigned from our available inventory and added to your account.
                      </div>
                      {selectedCountryForConfirmation?.code === 'CA' && (
                        <div className="text-white/60 text-xs mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                          🍁 Canadian numbers are perfect for businesses and users in Canada
                        </div>
                      )}
                      {selectedCountryForConfirmation?.code === 'GB' && (
                        <div className="text-white/60 text-xs mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                          🇬🇧 UK numbers are ideal for businesses and users in the United Kingdom
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleCountryBack}
                        disabled={isLoading}
                        className="flex-1 px-4 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur text-white/80 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Explore Other Countries
                      </button>
                      <button
                        onClick={handleCountryConfirm}
                        disabled={isLoading}
                        className="flex-1 px-4 py-3 rounded-xl bg-white/20 backdrop-blur border border-white/30 text-white font-medium hover:bg-white/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
                            Getting Number...
                          </>
                        ) : (
                          <>
                            Yes, Get {selectedCountryForConfirmation?.name} Number
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : showRandomConfirmation ? (
                  /* Random Confirmation View */
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mx-auto mb-4">
                        <ArrowPathIcon className="w-8 h-8 text-white/80" />
                      </div>
                      <div className="text-white/70 text-sm mb-6">
                        You'll receive a randomly assigned US phone number from our available inventory. 
                        This number will be added to your account immediately.
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleRandomBack}
                        disabled={isLoading}
                        className="flex-1 px-4 py-3 rounded-xl border border-white/20 bg-white/5 backdrop-blur text-white/80 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Choose Area Code Instead
                      </button>
                      <button
                        onClick={handleRandomConfirm}
                        disabled={isLoading}
                        className="flex-1 px-4 py-3 rounded-xl bg-white/20 backdrop-blur border border-white/30 text-white font-medium hover:bg-white/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
                            Getting Number...
                          </>
                        ) : (
                          'Yes, Get Random Number'
                        )}
                      </button>
                    </div>
                  </div>
                ) : showUSOptions ? (
                  /* US Options View */
                  <div className="space-y-4">
                    {/* Toll-Free Option (for eligible users) */}
                    {isTollFreeEligible() && (
                      <button
                        onClick={() => handleUSOptionSelect('toll_free')}
                        disabled={isLoading}
                        className="w-full p-4 rounded-xl border backdrop-blur-sm transition-all duration-200 flex items-center gap-4 bg-green-500/20 border-green-400/30 text-white hover:bg-green-500/30 hover:border-green-400/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        onMouseEnter={(e) => {
                          if (!isLoading) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isLoading) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-500/20 backdrop-blur border border-green-400/30 flex items-center justify-center">
                          <PhoneIcon className="w-6 h-6 text-green-400" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-white">
                            Choose Toll-Free Number
                          </div>
                          <div className="text-sm text-white/60 mt-1">
                            Browse 833, 844, 855, 866, 877, 888 numbers
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Random Number Option */}
                    <button
                      onClick={() => handleUSOptionSelect('random')}
                      disabled={isLoading}
                      className="w-full p-4 rounded-xl border backdrop-blur-sm transition-all duration-200 flex items-center gap-4 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                        <ArrowPathIcon className="w-6 h-6 text-white/80" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">
                          Get Random Number
                        </div>
                        <div className="text-sm text-white/60 mt-1">
                          We'll assign you an available number instantly
                        </div>
                      </div>
                    </button>

                    {/* Area Code Selection Option */}
                    <button
                      onClick={() => handleUSOptionSelect('area_code')}
                      disabled={isLoading}
                      className="w-full p-4 rounded-xl border backdrop-blur-sm transition-all duration-200 flex items-center gap-4 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                        <MapPinIcon className="w-6 h-6 text-white/80" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-white">
                          Choose Area Code/City
                        </div>
                        <div className="text-sm text-white/60 mt-1">
                          Search for numbers in a specific area or city
                        </div>
                      </div>
                    </button>
                  </div>
                ) : (
                  /* Countries List View */
                  <>
                    {loadingAvailability ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white/60 animate-spin"></div>
                        <span className="ml-3 text-white/70">Checking availability...</span>
                      </div>
                    ) : (
                      countries.map((country, index) => {
                        const isAvailable = availability[country.code] === true;
                        const isDisabled = isLoading || !isAvailable;
                        
                        return (
                          <button
                            key={country.code}
                            onClick={() => isAvailable ? handleCountryClick(country.code) : null}
                            disabled={isDisabled}
                            className={`w-full p-4 rounded-xl border backdrop-blur-sm transition-all duration-200 flex items-center gap-4 ${
                              isAvailable 
                                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30' 
                                : 'bg-white/5 border-white/10 text-white/40 cursor-not-allowed'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            style={{
                              animation: `slideInUp 0.4s ease-out ${index * 0.1}s both`
                            }}
                            onMouseEnter={(e) => {
                              if (!isDisabled && isAvailable) {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDisabled && isAvailable) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }
                            }}
                          >
                            <div className="flex-shrink-0">
                              <CountryFlag countryCode={country.code} size="lg" />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="font-medium">
                                {country.name}
                              </div>
                              <div className="text-xs text-white/60 mt-1">
                                {country.dialingCode}
                              </div>
                              {!isAvailable && !loadingAvailability && (
                                <div className="text-xs text-white/30 mt-1">
                                  Contact Support
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </>
                )}
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="mt-6 flex items-center justify-center gap-3 text-white/70">
                  <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white/60 animate-spin"></div>
                  <span className="text-sm font-medium">Getting your number...</span>
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </Dialog>
  );
};

CountrySelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectCountry: PropTypes.func.isRequired,
  onShowNumberSearch: PropTypes.func,
  onShowTollFreeSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  subscriptionInfo: PropTypes.object,
  userId: PropTypes.string
};

export default CountrySelectionModal; 